import { useCallback } from 'react';

import { useFormik } from 'formik';
import * as Yup from 'yup';

import { useL10n } from '@uplift/hooks';
import { l10nEnService } from '@uplift/services';
import { useCreateSupportRequestMutation } from '@uplift/queries';

interface SupportFormData {
  supportTopic: string;
  supportDetails: string;
}

const useValidationSchema = (l10n: typeof l10nEnService) => {
  return Yup.object().shape({
    supportTopic: Yup.string().required(l10n.support.validation.selectTopic),
    supportDetails: Yup.string().required(l10n.support.validation.moreDetails),
  });
};

export const useSupportRequestFormHook = (
  onSuccess: () => void,
  onError: () => void
) => {
  const createSupportRequestMutation = useCreateSupportRequestMutation();
  const onSubmit = useCallback(async (data, { resetForm }) => {
    try {
      await createSupportRequestMutation.mutateAsync({
        supportTypeId: data.supportTopic,
        problemText: data.supportDetails,
      });

      resetForm({});

      onSuccess();
    } catch (error) {
      onError();
    }
  }, []);

  const l10n = useL10n();
  const validationSchema = useValidationSchema(l10n);

  const initialFormState: SupportFormData = {
    supportTopic: '',
    supportDetails: '',
  };
  const formik = useFormik({
    initialValues: initialFormState,
    validationSchema,
    onSubmit,
  });

  return {
    formik,
    isFetching: createSupportRequestMutation.isLoading,
  };
};
